// Pure helper for computing lightbox image dimensions.
// No DOM / React — testable in isolation.

const MIN_PADDING = 48; // px on each side

export interface LightboxDimensions {
  width: number;
  height: number;
}

/**
 * Compute the rendered size for a lightbox image so that:
 * - It never exceeds the viewport minus MIN_PADDING on each side
 * - It preserves the original aspect ratio
 * - It never upscales beyond the image's natural size
 */
export function computeLightboxDimensions(
  naturalWidth: number,
  naturalHeight: number,
  viewport: { width: number; height: number },
): LightboxDimensions {
  if (naturalWidth <= 0 || naturalHeight <= 0) {
    return { width: 0, height: 0 };
  }

  const maxW = viewport.width - MIN_PADDING * 2;
  const maxH = viewport.height - MIN_PADDING * 2;

  // Don't upscale small images
  let w = Math.min(naturalWidth, maxW);
  let h = Math.min(naturalHeight, maxH);

  // Preserve aspect ratio: constrain to fit
  const aspectRatio = naturalWidth / naturalHeight;
  if (w / h > aspectRatio) {
    // Too wide for the height → constrain by height
    w = h * aspectRatio;
  } else {
    // Too tall for the width → constrain by width
    h = w / aspectRatio;
  }

  return {
    width: Math.round(w),
    height: Math.round(h),
  };
}
